// Pending "generating report..." rows — the shared machinery between the
// reports board (which WRITES optimistic rows on Generate) and the global
// header's Recent-Reports dropdown (which RENDERS them and reconciles them
// against arrived notifications).
//
// Legacy sources (ported 1:1):
//  - format map + helpers: universal_fn_tools.js:9-48 (DEFAULT_REPORT_FORMAT_
//    AVAILABLE; dev-sync G added the 34th entry), :967-969
//    (getAvailableFormatsForReport), :971-974 (getFormatFromUrl).
//  - row shape + write + same-tab CustomEvent('storage') bridge:
//    report/mvc/controller/action.js:112-182 (updateReportsData).
//  - reconciliation: header/view/header.js:777-836 (checkIsGenerated) +
//    :838-857 (compareNotifications); trigger points :718-747 (getReportsList)
//    on the 30s count longPoll (:749-768).
//  - render + FMI format normalization: :859-909 (renderPendingReports).
//  - one-time cleanup flag: header.js:194-197 ('pending_reports_removed').
//  - manual delete: header.js:924-935 (removePendingReport).

export const PENDING_REPORTS_KEY = 'pending_reports';
export const LAST_GENERATED_REPORT_KEY = 'last_generated_report';

/** Same-tab auto-open of the header Recent-Reports panel 5s after Generate
 *  (action.js:130-133 `$('.generated-reports-list').removeClass('hide')`). */
export const SHOW_REPORTS_LIST_EVENT = 'ksdm:show-generated-reports-list';

/**
 * Keyed by report VALUE (`ksdm_master_ahu_report.value`), NOT by display name.
 *
 * The legacy map (universal_fn_tools.js:9-48) was keyed by the display name,
 * which is master data a server can carry differently: its
 * "AHU Guide Specifications" key never matched the real name of
 * `ahu_guide_specs` ("Air Handling Unit Guide Specifications"), so that report
 * got ONE formatless pending row that no arriving notification could ever
 * match — "generating report..." forever. Values are code-side constants (the
 * dispatch table, the tab registry and the scope lists all key on them), so
 * they cannot drift the same way.
 */
export const REPORT_FORMATS_BY_VALUE: Record<string, string[]> = {
  weight_calc: ['pdf', 'csv'],
  electrical_calc: ['pdf', 'csv'],
  equipment_list: ['pdf', 'csv'],
  mfs_profile_cut_list: ['csv'],
  submittal_package: ['pdf'],
  submittal_cover: ['pdf'],
  pricing: ['pdf'],
  equipment_schedule: ['csv'],
  fan_data: ['pdf'],
  coil_data: ['pdf'],
  gas_heater_data: ['pdf'],
  evap_pack_data: ['pdf', 'csv'],
  bill_of_materials: ['pdf', 'csv'],
  pressure_drop_calc: ['pdf'],
  unit_technical_data: ['pdf', 'csv'],
  usage_statistics: ['pdf', 'csv'],
  // Dev-sync I (universal_fn_tools.js:26 on merged dev) — the coil-analytics
  // report generates a PDF + a CSV, like Usage Statistics.
  coil_analytics: ['pdf', 'csv'],
  integrated_project_ledger: ['zip'],
  proposal: ['docx'],
  ca_activities: ['csv'],
  ca_materials: ['csv'],
  ca_summary: ['csv'],
  sales_terms: ['pdf', 'docx'],
  shipping_terms: ['pdf', 'docx'],
  warranty_terms: ['pdf', 'docx'],
  ahu_guide_specs: ['pdf', 'docx'],
  condensing_unit_guide_specs: ['pdf', 'docx'],
  packaged_unit_guide_specs: ['pdf', 'docx'],
  scope_of_supply: ['pdf', 'docx'],
  center_of_gravity_calc: ['pdf'],
  ahu_acoustics: ['pdf'],
  plan: ['pdf'],
  plan_and_elevation_ns: ['pdf'],
  elevation_near_side: ['pdf'],
  elevation_far_side: ['pdf'],
  elevation_intake: ['pdf'],
  elevation_discharge: ['pdf'],
  // dev-sync G (universal_fn_tools.js:45-47): single AHU -> pdf, multiple
  // AHUs -> zip (see formatMatches' special case below, same pattern as
  // Proposal's docx/zip).
  fmi_assembly: ['pdf'],
};

/** universal_fn_tools.js:967-969, re-keyed on the report value. */
export function getAvailableFormatsForReport(reportValue: string | undefined): string[] | undefined {
  return reportValue ? REPORT_FORMATS_BY_VALUE[reportValue] : undefined;
}

/** universal_fn_tools.js:971-974 — trailing `.ext` of a download link. */
export function getFormatFromUrl(url: string | null | undefined): string {
  const match = (url ?? '').match(/\.([0-9a-z]+)$/i);
  return match ? match[1] : '';
}

/** One optimistic row (action.js:156-168). `started_at` round-trips through
 *  JSON as an ISO string; `last_id` is the newest notification id at write
 *  time. `allow_remove`/`index` are annotated by the header at render time
 *  (header.js:791 / :879). */
export interface PendingReportRow {
  project_name: string;
  report: string; // display name (current_report_name)
  /** Report VALUE (`current_report`). Everything that has to recognise a
   *  specific report keys on this; `report` is master data and is only used
   *  to match the notification's own `report` field (which is the same master
   *  name) and to render the row. Optional: rows written by an older build
   *  and still sitting in localStorage don't carry it. */
  report_value?: string;
  user_initials: string;
  finished_at: string; // 'generating report...'
  progress: boolean;
  last_id: number;
  started_at: string | Date;
  format: string;
  /** dev-sync G (action.js:165) — number of FMI drawing pages the backend
   *  will emit for this dispatch; >1 means the result is a zip. 0 for every
   *  other report. */
  fmi_page_count?: number;
  project_id?: string | null;
  ahu_id?: string | null;
  allow_remove?: boolean;
  index?: number;
  [key: string]: unknown;
}

/** Notification row subset the reconciliation reads (header.js:805-813). */
export interface ArrivedReportRow {
  id: number;
  project_name: string;
  report: string;
  user_initials?: string;
  download_link?: string | null;
  [key: string]: unknown;
}

export function readPendingReports(): PendingReportRow[] {
  try {
    const raw = localStorage.getItem(PENDING_REPORTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { data?: PendingReportRow[] } | null;
    return parsed?.data ?? [];
  } catch {
    return [];
  }
}

/** Silent write — compareNotifications persists WITHOUT re-dispatching the
 *  same-tab event (header.js:846-852); cross-tab listeners still get the
 *  native StorageEvent. Empty list removes the key (header.js:854). */
export function writePendingReports(rows: PendingReportRow[]): void {
  if (rows.length) {
    localStorage.setItem(PENDING_REPORTS_KEY, JSON.stringify({ data: rows }));
  } else {
    localStorage.removeItem(PENDING_REPORTS_KEY);
  }
}

/**
 * Write + notify this tab: the board's Generate flow persists the rows AND
 * fires a CustomEvent('storage') so the header on the SAME page re-renders
 * (action.js:170-181 — native storage events only fire in OTHER tabs). The
 * legacy also triggers a Backbone Dispatcher event; the CustomEvent is the
 * single React equivalent.
 */
export function writePendingReportsAndNotify(rows: PendingReportRow[]): void {
  writePendingReports(rows);
  window.dispatchEvent(
    new CustomEvent('storage', {
      detail: {
        key: PENDING_REPORTS_KEY,
        newValue: { data: rows },
        storageArea: localStorage,
        url: window.location.href,
      },
    })
  );
}

/**
 * Subscribe to pending-row changes from BOTH channels the legacy header binds
 * (header.js:361-370): the same-tab CustomEvent('storage') (payload in
 * `detail`) and the cross-tab native StorageEvent (string `newValue`).
 * Returns an unsubscribe.
 */
export function subscribePendingReports(
  onChange: (rows: PendingReportRow[]) => void
): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<{ key?: string; newValue?: unknown }>).detail;
    if (detail?.key === PENDING_REPORTS_KEY) {
      const value = detail.newValue;
      const parsed = typeof value === 'string' ? (JSON.parse(value) as { data?: PendingReportRow[] }) : (value as { data?: PendingReportRow[] } | null);
      onChange(parsed?.data ?? []);
      return;
    }
    const native = e as StorageEvent;
    if (native.key === PENDING_REPORTS_KEY) {
      try {
        const parsed = native.newValue ? (JSON.parse(native.newValue) as { data?: PendingReportRow[] }) : null;
        onChange(parsed?.data ?? []);
      } catch {
        onChange([]);
      }
    }
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

/** One-time stale-row purge on first header boot per browser
 *  (header.js:194-197). */
export function purgePendingReportsOnce(): void {
  if (!localStorage.getItem('pending_reports_removed')) {
    localStorage.removeItem(PENDING_REPORTS_KEY);
    localStorage.setItem('pending_reports_removed', 'true');
  }
}

/** `$('#user-initials').text().trim()` (action.js:103,159) — the header
 *  renders the span; both boards read it from the DOM. */
export function getUserInitials(): string {
  return document.getElementById('user-initials')?.textContent?.trim() ?? '';
}

// ---- Reconciliation (header.js:777-857) ----

/** Is this row the given report? By value, falling back to the display name
 *  for rows written by an older build (which carry no `report_value`). */
function reportIs(el: PendingReportRow, value: string, legacyName: string): boolean {
  return el.report_value ? el.report_value === value : el.report === legacyName;
}

// Proposal produces a docx for 1 AHU or a zip for many — match both
// (header.js:793-797).
const PROPOSAL_FORMATS = new Set(['docx', 'zip']);

/** dev-sync G (header.js:797-804) — "Fan + Motor + Isolator Assembly" is the
 *  same shape as Proposal: single AHU / single page -> pdf, multiple AHUs or
 *  multiple pages -> zip. */
export const FMI_REPORT_NAME = 'Fan + Motor + Isolator Assembly';
const FMI_FORMATS = new Set(['pdf', 'zip']);

function formatMatches(el: PendingReportRow, downloadLink: string | null | undefined): boolean {
  const actual = getFormatFromUrl(downloadLink);
  // A row with no format is a row whose formats we could not look up (a report
  // missing from REPORT_FORMATS_BY_VALUE, or an old row from before this map
  // was keyed by value). Clearing it on the first matching notification is the
  // self-healing half of the "generating report..." forever bug: the row may
  // under-count the delivered files, but it never sticks.
  if (!el.format) return true;
  return (
    el.format === actual ||
    (reportIs(el, 'proposal', 'Proposal') && PROPOSAL_FORMATS.has(actual)) ||
    (reportIs(el, 'fmi_assembly', FMI_REPORT_NAME) && FMI_FORMATS.has(actual))
  );
}

/**
 * Reports whose pending rows match a notification regardless of project name
 * (header.js:807-809; dev-sync I adds Coil Analytics — header.js:806-810 on
 * merged dev — it is a global aggregate report with no project scope).
 *
 * The six CMS documents belong here too: they are manufacturer-wide, so
 * `create_notificaton` (tasks/call_reports.py:13-30) leaves the notification's
 * project_name empty, while the board stamps the project selector's current
 * label ('All' by default) onto the pending row — a mismatch that kept every
 * Terms & Conditions / Guide Specifications row pending forever.
 */
const PROJECTLESS_VALUES = new Set([
  'usage_statistics',
  'integrated_project_ledger',
  'coil_analytics',
  'sales_terms',
  'shipping_terms',
  'warranty_terms',
  'ahu_guide_specs',
  'condensing_unit_guide_specs',
  'packaged_unit_guide_specs',
]);

/** Display-name fallback for rows written before `report_value` existed. */
const PROJECTLESS_NAMES = new Set([
  'Usage Statistics',
  'Integrated Project Ledger',
  'Coil Analytics',
]);

function isProjectless(el: PendingReportRow): boolean {
  return el.report_value
    ? PROJECTLESS_VALUES.has(el.report_value)
    : PROJECTLESS_NAMES.has(el.report);
}

function rowMatchesNotification(el: PendingReportRow, r: ArrivedReportRow): boolean {
  return (
    (r.project_name === el.project_name || isProjectless(el)) &&
    r.report === el.report &&
    r.user_initials === el.user_initials &&
    el.last_id < r.id &&
    formatMatches(el, r.download_link)
  );
}

/**
 * checkIsGenerated (header.js:777-836), ported literally including the
 * duplicate-id second chance: a pending row is CLEARED when a newer
 * (id > last_id) notification with the same project+report+user+format
 * exists and hasn't already been claimed by another pending row; a row whose
 * only candidate was claimed bumps its last_id and stays pending.
 * Rows that survive get `allow_remove: true` (→ the Delete action).
 */
function checkIsGenerated(
  el: PendingReportRow,
  ids: number[],
  pendingReports: PendingReportRow[],
  reportsList: ArrivedReportRow[]
): void {
  el.allow_remove = true;
  const exist = reportsList.find((r) => rowMatchesNotification(el, r));
  if (exist && !ids.includes(exist.id)) {
    ids.push(exist.id);
  } else if (exist && ids.includes(exist.id)) {
    // Legacy re-searches with an extra `!ids.includes(el.id)` clause
    // (header.js:818-828); pending rows carry no `id`, so the re-search finds
    // the same row and the id is pushed again — reproduced as-is.
    const checkExist = reportsList.find(
      (r) => rowMatchesNotification(el, r) && !ids.includes(el.id as unknown as number)
    );
    if (checkExist) {
      ids.push(exist.id);
    } else {
      el.last_id = exist.id;
      pendingReports.push(el);
    }
  } else {
    pendingReports.push(el);
  }
}

/** compareNotifications core (header.js:838-842): returns the rows that are
 *  STILL pending after matching against the arrived notification list. The
 *  caller persists (writePendingReports) and updates the badge. */
export function reconcilePendingReports(
  reportsList: ArrivedReportRow[],
  rows: PendingReportRow[]
): PendingReportRow[] {
  const ids: number[] = [];
  const stillPending: PendingReportRow[] = [];
  rows.forEach((el) => checkIsGenerated({ ...el }, ids, stillPending, reportsList));
  return stillPending;
}

/**
 * dev-sync G — normalizePendingFormat (header.js:864-873). An FMI row whose
 * dispatch covered more than one drawing page is delivered as a zip, so a row
 * that says anything else (including a formatless row) is rewritten to `zip`
 * BEFORE the fan-out. Returns [row, rewritten]; the row is copied only when
 * it actually changes, so non-FMI rows keep their identity.
 */
function normalizePendingFormat(row: PendingReportRow): [PendingReportRow, boolean] {
  if (
    reportIs(row, 'fmi_assembly', FMI_REPORT_NAME) &&
    Number(row.fmi_page_count || 0) > 1 &&
    row.format !== 'zip'
  ) {
    return [{ ...row, format: 'zip' }, true];
  }
  return [row, false];
}

/**
 * renderPendingReports format expansion (header.js:859-909): rows written
 * without a format (legacy paths outside the board) fan out into one row per
 * available format. Returns [rows, changed]; the caller persists when changed.
 */
export function expandFormatlessRows(
  rows: PendingReportRow[]
): [PendingReportRow[], boolean] {
  let changed = false;
  const out: PendingReportRow[] = [];
  rows.forEach((raw) => {
    // dev-sync G (header.js:878) — normalize BEFORE the formatless check, so a
    // multi-page FMI row becomes ONE 'zip' row instead of fanning out to
    // 'pdf'. This is also what makes the explicit `!report.format` FMI branch
    // at header.js:881-887 unreachable in the legacy; not reproduced because
    // it can never run.
    const [row, rewritten] = normalizePendingFormat(raw);
    if (rewritten) changed = true;
    if (!row.format) {
      const formats = getAvailableFormatsForReport(row.report_value) || [''];
      formats.forEach((format) => {
        // header.js:892-893 re-normalizes each copy then falls back to the map
        // format (`new_report.format || format`). The outer normalize already
        // ran, so the re-normalize is a no-op — the fallback is the live half.
        out.push({ ...row, format: row.format || format });
      });
      changed = true; // legacy splices + re-persists on every expansion (:894-896, :902-907)
      return;
    }
    out.push(row);
  });
  return [out, changed];
}

/** removePendingReport (header.js:924-935) — Delete on a pending row splices
 *  by its rendered index. */
export function removePendingReportAt(index: number): PendingReportRow[] {
  const rows = readPendingReports();
  rows.splice(index, 1);
  localStorage.setItem(PENDING_REPORTS_KEY, JSON.stringify({ data: rows }));
  return rows;
}
